import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  IndianRupee,
  Check,
  X,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { plansAPI } from '../../services/api';
import { usePrivacy } from '../../contexts/PrivacyContext';

const PlansPage = () => {
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const [plans, setPlans] = useState([]);
  const { confirm, dialogProps } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [newPlan, setNewPlan] = useState({
    plan_name: '',
    base_user_capacity: 50,
    base_price_per_month: 10000,
    per_extra_user_price_per_month: 150,
    billing_cycle_type: 'MONTHLY',
    is_active: true
  });

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const res = await plansAPI.list(false);
        setPlans(res.data || []);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleCreatePlan = async () => {
    try {
      const res = await plansAPI.create(newPlan);
      setPlans([...plans, res.data]);
      setIsCreateOpen(false);
      setNewPlan({
        plan_name: '',
        base_user_capacity: 50,
        base_price_per_month: 10000,
        per_extra_user_price_per_month: 150,
        billing_cycle_type: 'MONTHLY',
        is_active: true
      });
    } catch (error) {
      console.error('Failed to create plan:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create plan',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleUpdatePlan = async () => {
    if (!editingPlan) return;
    try {
      await plansAPI.update(editingPlan.id, editingPlan);
      setPlans(plans.map(p => p.id === editingPlan.id ? editingPlan : p));
      setEditingPlan(null);
    } catch (error) {
      console.error('Failed to update plan:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to update plan',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDeletePlan = async (planId) => {
    const ok = await confirm({
      title: 'Delete plan?',
      description: 'This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true
    });
    if (!ok) return;
    try {
      await plansAPI.delete(planId);
      setPlans(plans.filter(p => p.id !== planId));
    } catch (error) {
      console.error('Failed to delete plan:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading plans...</span>
      </div>
    );
  }

  const togglePlanStatus = async (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;
    try {
      await plansAPI.update(planId, { is_active: !plan.is_active });
      setPlans(plans.map(p => p.id === planId ? { ...p, is_active: !p.is_active } : p));
    } catch (error) {
      console.error('Failed to toggle plan status:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Subscription Plans</h1>
          <p className="text-slate-500">Manage HRMS subscription plans and pricing</p>
        </div>
        <Button 
          className="bg-slate-800 hover:bg-slate-900 text-white"
          onClick={() => setIsCreateOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Plan
        </Button>
      </div>

      {/* Plans Table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-700">Plan Name</TableHead>
                <TableHead className="font-semibold text-slate-700">Base Capacity</TableHead>
                <TableHead className="font-semibold text-slate-700">Base Price</TableHead>
                <TableHead className="font-semibold text-slate-700">Extra User Price</TableHead>
                <TableHead className="font-semibold text-slate-700">Billing Cycle</TableHead>
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id} className="hover:bg-slate-50">
                  <TableCell>
                    <span className="font-medium text-slate-800">{plan.plan_name}</span>
                  </TableCell>
                  <TableCell>{plan.base_user_capacity} users</TableCell>
                  <TableCell className="font-medium">
                    {formatCurrency(plan.base_price_per_month)}/mo
                  </TableCell>
                  <TableCell>
                    {formatCurrency(plan.per_extra_user_price_per_month)}/user/mo
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{plan.billing_cycle_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Switch 
                      checked={plan.is_active}
                      onCheckedChange={() => togglePlanStatus(plan.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditingPlan(plan)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDeletePlan(plan.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Plan Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create New Plan</DialogTitle>
            <DialogDescription>
              Add a new subscription plan for HRMS.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Plan Name *</Label>
              <Input
                value={newPlan.plan_name}
                onChange={(e) => setNewPlan({...newPlan, plan_name: e.target.value})}
                placeholder="e.g., Enterprise"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Base User Capacity</Label>
                <Input
                  type="number"
                  value={newPlan.base_user_capacity}
                  onChange={(e) => setNewPlan({...newPlan, base_user_capacity: parseInt(e.target.value)})}
                />
              </div>
              <div className="grid gap-2">
                <Label>Base Price (per month)</Label>
                <Input
                  type="number"
                  value={newPlan.base_price_per_month}
                  onChange={(e) => setNewPlan({...newPlan, base_price_per_month: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Per Extra User Price</Label>
                <Input
                  type="number"
                  value={newPlan.per_extra_user_price_per_month}
                  onChange={(e) => setNewPlan({...newPlan, per_extra_user_price_per_month: parseInt(e.target.value)})}
                />
              </div>
              <div className="grid gap-2">
                <Label>Billing Cycle</Label>
                <Select 
                  value={newPlan.billing_cycle_type} 
                  onValueChange={(v) => setNewPlan({...newPlan, billing_cycle_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                    <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                    <SelectItem value="HALF_YEARLY">Half Yearly</SelectItem>
                    <SelectItem value="YEARLY">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePlan}
              disabled={!newPlan.plan_name}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Create Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Plan Modal */}
      <Dialog open={!!editingPlan} onOpenChange={() => setEditingPlan(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Plan</DialogTitle>
          </DialogHeader>
          {editingPlan && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Plan Name *</Label>
                <Input
                  value={editingPlan.plan_name}
                  onChange={(e) => setEditingPlan({...editingPlan, plan_name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Base User Capacity</Label>
                  <Input
                    type="number"
                    value={editingPlan.base_user_capacity}
                    onChange={(e) => setEditingPlan({...editingPlan, base_user_capacity: parseInt(e.target.value)})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Base Price (per month)</Label>
                  <Input
                    type="number"
                    value={editingPlan.base_price_per_month}
                    onChange={(e) => setEditingPlan({...editingPlan, base_price_per_month: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Per Extra User Price</Label>
                  <Input
                    type="number"
                    value={editingPlan.per_extra_user_price_per_month}
                    onChange={(e) => setEditingPlan({...editingPlan, per_extra_user_price_per_month: parseInt(e.target.value)})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Billing Cycle</Label>
                  <Select 
                    value={editingPlan.billing_cycle_type} 
                    onValueChange={(v) => setEditingPlan({...editingPlan, billing_cycle_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MONTHLY">Monthly</SelectItem>
                      <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                      <SelectItem value="HALF_YEARLY">Half Yearly</SelectItem>
                      <SelectItem value="YEARLY">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPlan(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdatePlan}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>

      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />

      </Dialog>
    </div>
  );
};

export default PlansPage;
